import { forwardRef } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { tokens } from "../../../../theme";

export interface MemorialGraphic1080Props {
  fullName: string;
  birthDate: string;
  deathDate: string;
  funeralDateText: string;
  venueText: string;
  imageUrl: string;
  patternSvgUrl: string;
  livestreamedBy?: string;
  topLine?: string;
  secondLine?: string;
  funeralStartTime?: string;
  imageSize?: number;
  patternSize?: number;
  previewSize?: number;
}

const BASE_SIZE = 1080;

const MemorialGraphic1080 = forwardRef<
  HTMLDivElement,
  MemorialGraphic1080Props
>(
  (
    {
      fullName,
      birthDate,
      deathDate,
      funeralDateText,
      venueText,
      imageUrl,
      patternSvgUrl,
      funeralStartTime,
      livestreamedBy = "MUZUKURU",
      topLine = "In loving",
      secondLine = "MEMORY OF",
      imageSize = 355,
      patternSize = 520,
      previewSize = 300,
    },
    exportRef,
  ) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const scale = previewSize / BASE_SIZE;

    return (
      <Box
        sx={{
          width: previewSize,
          height: previewSize,
          position: "relative",
          overflow: "hidden",
          mx: "auto",
          backgroundColor: colors.primary[900],
        }}
      >
        <Box
          ref={exportRef}
          sx={{
            width: BASE_SIZE,
            height: BASE_SIZE,
            position: "absolute",
            top: 0,
            left: 0,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            overflow: "hidden",
            background: colors.primary[900],
          }}
        >
          {/* soft paper texture */}
          {/* <Box
            sx={{
              position: "absolute",
              inset: 0,
              opacity: 0.07,
              pointerEvents: "none",
              backgroundImage: `
                radial-gradient(rgba(80,80,80,0.22) 0.7px, transparent 0.7px),
                radial-gradient(rgba(120,120,120,0.15) 0.6px, transparent 0.6px)
              `,
              backgroundSize: "12px 12px, 18px 18px",
              backgroundPosition: "0 0, 6px 6px",
            }}
          /> */}

          {/* heading */}
          <Box
            sx={{
              position: "absolute",
              top: 36,
              left: 0,
              right: 0,
              textAlign: "center",
              zIndex: 3,
            }}
          >
            <Typography
              sx={{
                fontFamily: `'Le Jour Script', 'Brush Script MT', cursive`,
                fontSize: "120px",
                lineHeight: 0.84,
                color: "#5a5b51",
                fontWeight: 400,
                letterSpacing: "0.01em",
              }}
            >
              {topLine}
            </Typography>

            <Typography
              sx={{
                mt: 2.5,
                fontSize: "27px",
                letterSpacing: "0.28em",
                fontWeight: 400,
                color: "#5d5d55",
                textTransform: "uppercase",
              }}
            >
              {secondLine}
            </Typography>
          </Box>

          {/* wreath + portrait */}
          <Box
            sx={{
              position: "absolute",
              top: 182,
              left: "50%",
              transform: "translateX(-50%)",
              width: patternSize,
              height: patternSize,
              zIndex: 2,
            }}
          >
            <Box
              component="img"
              src={patternSvgUrl}
              alt="Memorial wreath"
              crossOrigin="anonymous"
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: patternSize,
                height: patternSize,
                objectFit: "contain",
                zIndex: 1,
                pointerEvents: "none",
                userSelect: "none",
              }}
            />

            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: imageSize,
                height: imageSize,
                borderRadius: "50%",
                overflow: "hidden",
                zIndex: 2,
                backgroundColor: "#d9d9d6",
              }}
            >
              <Box
                component="img"
                src={imageUrl}
                alt={fullName}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                  filter: "none",
                }}
              />
            </Box>
          </Box>

          {/* name and dates */}
          <Box
            sx={{
              position: "absolute",
              top: 730,
              left: 0,
              right: 0,
              textAlign: "center",
              zIndex: 3,
              px: 8,
            }}
          >
            <Typography
              sx={{
                fontSize: "46px",
                fontWeight: 400,
                letterSpacing: "0.12em",
                color: "#56584f",
                textTransform: "uppercase",
                lineHeight: 1.1,
              }}
            >
              {fullName}
            </Typography>

            <Typography
              sx={{
                mt: 1.5,
                fontSize: "22px",
                color: "#68675f",
                letterSpacing: "0.12em",
                fontWeight: 400,
                lineHeight: 1.2,
              }}
            >
              {birthDate} - {deathDate}
            </Typography>
          </Box>

          {/* service details */}
          <Box
            sx={{
              position: "absolute",
              top: 850,
              left: 0,
              right: 0,
              textAlign: "center",
              zIndex: 3,
              px: 10,
            }}
          >
            <Typography
              sx={{
                fontSize: "22px",
                letterSpacing: "0.14em",
                color: "#57574f",
                textTransform: "uppercase",
                lineHeight: 1.55,
                fontWeight: 400,
              }}
            >
              {funeralDateText}, AT {funeralStartTime}HRS (CAT)
            </Typography>

            <Typography
              sx={{
                mt: 0.5,
                fontSize: "21px",
                letterSpacing: "0.15em",
                color: "#5e5e57",
                textTransform: "uppercase",
                lineHeight: 1.45,
                fontWeight: 400,
              }}
            >
              {venueText}
            </Typography>
          </Box>

          {/* footer */}
          <Box
            sx={{
              position: "absolute",
              bottom: 64,
              left: 0,
              right: 0,
              textAlign: "center",
              zIndex: 3,
              px: 6,
            }}
          >
            <Typography
              sx={{
                fontSize: "18px",
                letterSpacing: "0.24em",
                color: "#3d3d37",
                textTransform: "uppercase",
                fontWeight: 400,
              }}
            >
              This service is being livestreamed by
            </Typography>

            <Box
              component="img"
              src={livestreamedBy}
              alt="Livestreamed by"
              sx={{
                mt: 2,
                height: "52px",
                width: "auto",
                objectFit: "contain",
              }}
            />
          </Box>
        </Box>
      </Box>
    );
  },
);

MemorialGraphic1080.displayName = "MemorialGraphic1080";

export default MemorialGraphic1080;
